import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Grid,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { RoomApiData } from "../../../../types/venue";

import cocktailImg from "../../../../assets/venue-layouts/cocktail.jpg";
import classroomImg from "../../../../assets/venue-layouts/classroom.png";
import theatreImg from "../../../../assets/venue-layouts/theatre.png";
import banquetImg from "../../../../assets/venue-layouts/banquet.png";
import ushapeImg from "../../../../assets/venue2.jpg";
import { PictureAsPdf } from "@mui/icons-material";

const LAYOUT_IMAGES: Record<string, string> = {
  theater: theatreImg,
  theatre: theatreImg,
  "u-shape": ushapeImg,
  ushape: ushapeImg,
  classroom: classroomImg,
  banquet: banquetImg,
  cocktail: cocktailImg,
  hall: theatreImg,
  conference: classroomImg,
};

interface RoomDetailsProps {
  rooms: RoomApiData[];
  onEditClick: (room: RoomApiData) => void;
}

function DetailCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        backgroundColor: "#f4f6f8",
        mb: 1.5,
      }}
    >
      <Box sx={{ color: "#888", display: "flex", alignItems: "center" }}>
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: "0.6rem",
            color: "#999",
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            lineHeight: 1,
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#222" }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Paper>
  );
}

function LayoutCard({
  layout,
}: {
  layout: { id: string; name: string; capacity: number };
}) {
  const key = layout.id?.toLowerCase().replace(/\s/g, "-");
  const img = LAYOUT_IMAGES[key] ?? LAYOUT_IMAGES[layout.name?.toLowerCase()];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        minWidth: 80,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 52,
          borderRadius: 1.5,
          overflow: "hidden",
          border: "1px solid #eee",
          backgroundColor: "#f0f0f0",
        }}
      >
        {img ? (
          <img
            src={img}
            alt={layout.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%", bgcolor: "#e0e0e0" }} />
        )}
      </Box>
      <Typography
        sx={{ fontSize: "0.7rem", color: "#888", textAlign: "center" }}
      >
        {layout.name}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#222" }}>
        {layout.capacity}
      </Typography>
    </Box>
  );
}

export default function RoomDetails({ rooms, onEditClick }: RoomDetailsProps) {
  const [activeTab, setActiveTab] = useState(0);

  const safeRooms = Array.isArray(rooms) ? rooms : [];

  if (safeRooms.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">
          No room information available.
        </Typography>
      </Box>
    );
  }

  const currentRoom = safeRooms[activeTab];

  // layouts from RoomApiData come from VenueLayout (id: number, layout_type, capacity)
  // filter those with capacity > 0
  const activeLayouts = (currentRoom.layouts ?? []).filter(
    (l) => Number(l.capacity) > 0,
  );

  // photos and floorplans come from files array
  const photos = (currentRoom.files ?? []).filter(
    (f) => f.file_type === "venue_photos",
  );
  const floorplans = (currentRoom.files ?? []).filter(
    (f) => f.file_type === "floor_plan",
  );

  return (
    <Box sx={{ p: 1 }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          borderBottom: "1px solid #e0e0e0",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.85rem",
            color: "#888",
            minWidth: "auto",
            px: 2,
            "&.Mui-selected": { color: "#222", fontWeight: 700 },
          },
          "& .MuiTabs-indicator": { backgroundColor: "#222", height: 2 },
        }}
      >
        {safeRooms.map((room, index) => (
          <Tab
            key={room.id || index}
            label={room.name || `Room ${index + 1}`}
          />
        ))}
      </Tabs>

      <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
        {/* Left: Room Details */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "10px",
              border: "1px solid #eaeaea",
              height: "100%",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#222" }}
              >
                Room Details
              </Typography>
              <IconButton
                size="small"
                onClick={() => onEditClick?.(currentRoom)}
                sx={{ color: "#888" }}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <DetailCard
              icon={<PeopleOutlineIcon sx={{ fontSize: 20 }} />}
              label="Capacity"
              value={currentRoom.capacity?.toString()}
            />

            <DetailCard
              icon={<WifiOutlinedIcon sx={{ fontSize: 20 }} />}
              label="Internet Speed"
              value={
                currentRoom.internet_upload_speed
                  ? `${currentRoom.internet_upload_speed} Mbps`
                  : null
              }
            />
            <DetailCard
              icon={<HomeOutlinedIcon sx={{ fontSize: 20 }} />}
              label="Venue Type"
              value={currentRoom.venue_type}
            />
            <DetailCard
              icon={<WifiOutlinedIcon sx={{ fontSize: 20 }} />}
              label="WiFi Name"
              value={currentRoom.wifi_name}
            />
            <DetailCard
              icon={<WifiOutlinedIcon sx={{ fontSize: 20 }} />}
              label="ISP"
              value={currentRoom.internet_service_provider}
            />
          </Paper>
        </Grid>

        {/* Right: Layouts + Photos */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 2,
            }}
          >
            {/* Layouts */}
            <Paper
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                borderRadius: "10px",
                border: "1px solid #eee",
                px: 2,
                py: 1.5,
                boxShadow: "none",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  color: "#222",
                  mb: 1.5,
                }}
              >
                Layouts
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {activeLayouts.length > 0 ? (
                  activeLayouts.map((layout, i) => (
                    <LayoutCard
                      key={layout.id ?? i}
                      layout={{
                        id: layout.layout_type ?? String(layout.id),
                        name: layout.layout_type ?? "Layout",
                        capacity: Number(layout.capacity),
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No layouts configured.
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Photos */}
            {/* Photos & Floorplans Section */}
            <Paper
              sx={{
                flex: 2,
                display: "flex",
                flexDirection: "column",
                borderRadius: "10px",
                border: "1px solid #eee",
                p: 2,
                backgroundColor: "#fff",
                boxShadow: "none",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  color: "#222",
                  mb: 1.5,
                }}
              >
                Room Photos and Floorplans
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flex: 1,
                }}
              >
                {photos.length > 0 || floorplans.length > 0 ? (
                  <>
                    {/* Gallery Grid Wrapper */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      {/* Render Room Photos */}
                      {photos.map((photo) => (
                        <Box
                          key={photo.id}
                          component="img"
                          src={photo.file}
                          alt="room photo"
                          sx={{
                            width: 80,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: 1.5,
                            border: "1px solid #eee",
                          }}
                        />
                      ))}

                      {/* Render Floor Plans */}
                      {/* Render Floor Plans */}
                      {floorplans.map((floorplan) => {
                        const isPdf =
                          floorplan.file?.toLowerCase().endsWith(".pdf") ||
                          floorplan.file?.includes("application/pdf");

                        return isPdf ? (
                          <Box
                            key={floorplan.id}
                            onClick={() =>
                              window.open(floorplan.file, "_blank")
                            }
                            sx={{
                              width: 80,
                              height: 60,
                              borderRadius: 1.5,
                              border: "1px solid #eee",
                              backgroundColor: "#fafafa",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              gap: 0.5,
                              "&:hover": { backgroundColor: "#f0f0f0" },
                            }}
                          >
                            <PictureAsPdf
                              sx={{ fontSize: 22, color: "#C29363" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.55rem",
                                color: "#999",
                                fontWeight: 600,
                              }}
                            >
                              PDF
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            key={floorplan.id}
                            component="img"
                            src={floorplan.file}
                            alt="floor plan"
                            sx={{
                              width: 80,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 1.5,
                              border: "1px solid #eee",
                              backgroundColor: "#fafafa",
                            }}
                          />
                        );
                      })}
                    </Box>

                    {/* Counter Text - neatly aligned to the bottom-left */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: "0.8rem",
                        mt: "auto",
                        fontWeight: 500,
                      }}
                    >
                      {photos.length} {photos.length === 1 ? "Photo" : "Photos"}{" "}
                      · {floorplans.length}{" "}
                      {floorplans.length === 1 ? "Floorplan" : "Floorplans"}
                    </Typography>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#bbb" }}>
                      No photos or floorplans uploaded for this room.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
